import type { PortfolioTenant, Severity, Tenant, Verdict } from './api.ts';
import { plural } from './format.ts';

/**
 * The Clients table's row model: what a client's figures mean, and the order
 * they are read in.
 *
 * Split out of `Portfolio.tsx` when the client lifecycle merged into that
 * screen and it passed the 800-line ceiling. Nothing here changed in the move —
 * the tiers, their sentences and the four comparators are the same ones that
 * shipped — but it is a plain module now rather than a `.tsx` one, which is the
 * difference between logic the suite can assert about and logic it cannot (the
 * console's `.tsx` files are excluded; see vitest.config.ts).
 */

const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export const SEVERITY_TEXT: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * The four verdicts in the order a reader needs them, each carrying the badge
 * class that draws it. The glyph and the fill come from `.verdict` in
 * styles.css, so this table holds only the word and the count that goes beside
 * it — four screens cannot disagree about which character means "not met".
 */
export const VERDICTS: ReadonlyArray<{ verdict: Verdict; className: string; label: string }> = [
  { verdict: 'FAIL', className: 'verdict-not-met', label: 'Not met' },
  { verdict: 'INSUFFICIENT_DATA', className: 'verdict-not-evidenced', label: 'Not evidenced' },
  { verdict: 'PASS', className: 'verdict-met', label: 'Met' },
  { verdict: 'NOT_APPLICABLE', className: 'verdict-not-applicable', label: 'Not applicable' },
];

/** What the MSP does about a broken pipe, which differs by how it broke. */
const BROKEN_ACTION: Record<Tenant['connectionState'], string> = {
  consent_revoked: 'Consent has been withdrawn. Send a fresh consent link before anything here can be refreshed.',
  insufficient_permissions:
    'Consent was granted without the permissions we read with. A Global Administrator needs to grant it again.',
  error: 'The last connection attempt failed. Check the tenant, then retry.',
  connected: '',
  pending_consent: '',
};

/** Exported so `clientViews.ts` measures a wait in the same unit this file ages a scan in. */
export const DAY_MS = 86_400_000;

const parsedTime = (iso: string | null): number | null => {
  if (iso === null) return null;
  const value = Date.parse(iso);
  return Number.isNaN(value) ? null : value;
};

export const asAt = (epoch: number): string => new Date(epoch).toLocaleDateString('en-GB');

export const relativeDays = (days: number): string => {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
};

/**
 * An assessment older than this is still real, but it is describing a tenant as
 * it was a month ago. Said out loud rather than left for the reader to work out
 * from a date, because "is this current?" is the question behind every other
 * one here.
 */
export const STALE_DAYS = 35;

/**
 * The marker each attention state draws. Colour is never the only carrier: the
 * shape of the marker differs with the colour and the word is always written
 * out, so the column survives greyscale and a colour vision deficiency.
 */
export type Flag = 'blocked' | 'failing' | 'unassessed' | 'open' | 'clear' | 'waiting';

export interface Assessment {
  /** Lower is more urgent. The ordering rule lives on the tier comments below. */
  tier: number;
  label: string;
  flag: Flag;
  action: string;
}

/**
 * The default order, and the only part of this screen worth arguing about.
 *
 * 0 — Cannot assess. The connection was working and has stopped: consent
 *     revoked, permissions withdrawn, or a hard error. This outranks even a
 *     critical failure because it invalidates every other number on the row —
 *     those counts describe a tenant we are no longer watching, and no scan
 *     will correct them until a human acts. A screen whose figures might be
 *     lying has to say so before it ranks anything.
 * 1 — Failing controls, ordered by the worst failing severity and then by how
 *     many. This is the severity-before-volume rule: three critical failures
 *     outrank ten low ones, because ten low findings are a backlog and one
 *     critical is an incident. Severity comes from the catalogue, not from us.
 * 2 — Connected but never assessed. Zero knowledge of a tenant we do have
 *     access to. Ranked below known failures because it is one click from
 *     being answered, and above everything below because "no findings" and
 *     "no assessment" look identical in a count and are nothing alike.
 * 3 — Assessed, nothing failing, but controls we could not evidence.
 *     Not evidenced is not a pass. It is usually a licence gap, which is a
 *     commercial conversation rather than a security one, so it sits below
 *     failures — but above clear, because the coverage claim is incomplete.
 * 4 — Clear at the last assessment. Oldest first within the tier: a clear
 *     result from March is a weaker claim than a clear result from yesterday.
 * 5 — Awaiting consent. Deliberately last despite being unassessable. A
 *     pending tenant is a rollout in progress, not a regression, and this table
 *     is not where it is worked — the Awaiting consent tab tracks who has been
 *     asked and when. Sorting an unfinished rollout above a client with live
 *     critical failures would make the screen useless during the weeks it is
 *     most used.
 *
 * Ties inside a tier break on staleness, oldest first, then on name so the
 * order is stable between refreshes.
 */
const assess = (tenant: PortfolioTenant): Assessment => {
  const state = tenant.connectionState;
  const fails = tenant.counts.FAIL ?? 0;
  const unevidenced = tenant.counts.INSUFFICIENT_DATA ?? 0;

  if (state !== 'connected' && state !== 'pending_consent') {
    return {
      tier: 0,
      label: 'Cannot assess',
      flag: 'blocked',
      action: tenant.connectionDetail ?? BROKEN_ACTION[state],
    };
  }

  if (state === 'pending_consent') {
    return {
      tier: 5,
      label: tenant.invitedAt === null ? 'Not invited yet' : 'Awaiting the customer',
      flag: 'waiting',
      action:
        tenant.invitedAt === null
          ? 'Nobody has been asked yet. Send the consent link from the Awaiting consent tab.'
          : `Link sent, consent not yet granted${tenant.reminderCount > 0 ? ` after ${plural(tenant.reminderCount, 'reminder')}` : ''}. Chase it on the Awaiting consent tab.`,
    };
  }

  if (fails > 0) {
    const worst = tenant.worstFailingSeverity;
    return {
      tier: 1,
      label: worst === null ? 'Failing controls' : `${SEVERITY_TEXT[worst]} failures`,
      flag: 'failing',
      action: `${plural(fails, 'control')} not met at the last assessment. Open the client to see which.`,
    };
  }

  if (tenant.lastScannedAt === null) {
    return {
      tier: 2,
      label: 'Never assessed',
      flag: 'unassessed',
      action: 'Connected and ready. Nothing has been assessed yet — run the first assessment.',
    };
  }

  if (unevidenced > 0) {
    return {
      tier: 3,
      label: 'Partly evidenced',
      flag: 'open',
      action: `Nothing failing, but ${plural(unevidenced, 'control')} could not be determined — usually a licence gap.`,
    };
  }

  return {
    tier: 4,
    label: 'Clear',
    flag: 'clear',
    action: 'Nothing failing and nothing unevidenced at the last assessment.',
  };
};

export interface Row {
  tenant: PortfolioTenant;
  risk: Assessment;
  counts: Record<Verdict, number>;
  fails: number;
  /** Epoch milliseconds, or null where the client has never been assessed. */
  scannedAt: number | null;
  /** Whole days since the last assessment, or null. */
  age: number | null;
  /** The connection has broken since the last assessment; the figures are frozen. */
  broken: boolean;
}

export const toRow = (tenant: PortfolioTenant, now: number): Row => {
  const scannedAt = parsedTime(tenant.lastScannedAt);
  const state = tenant.connectionState;
  return {
    tenant,
    risk: assess(tenant),
    counts: {
      FAIL: tenant.counts.FAIL ?? 0,
      PASS: tenant.counts.PASS ?? 0,
      INSUFFICIENT_DATA: tenant.counts.INSUFFICIENT_DATA ?? 0,
      NOT_APPLICABLE: tenant.counts.NOT_APPLICABLE ?? 0,
    },
    fails: tenant.counts.FAIL ?? 0,
    scannedAt,
    age: scannedAt === null ? null : Math.floor((now - scannedAt) / DAY_MS),
    broken: state !== 'connected' && state !== 'pending_consent',
  };
};

const compare = (a: number, b: number): number => (a < b ? -1 : a > b ? 1 : 0);

const byName = (a: Row, b: Row): number =>
  a.tenant.displayName.localeCompare(b.tenant.displayName, 'en-GB');

/** Never assessed sorts as the stalest thing on the screen, not as the newest. */
const staleness = (row: Row): number => (row.scannedAt === null ? -Infinity : row.scannedAt);

const severityRank = (row: Row): number =>
  row.tenant.worstFailingSeverity === null ? 4 : SEVERITY_RANK[row.tenant.worstFailingSeverity];

export type SortKey = 'risk' | 'name' | 'scanned' | 'failures';

interface SortSpec {
  /** The aria-sort value this comparator produces before the user reverses it. */
  direction: 'ascending' | 'descending';
  compare: (a: Row, b: Row) => number;
}

export const SORTS: Record<SortKey, SortSpec> = {
  risk: {
    direction: 'descending',
    compare: (a, b) =>
      compare(a.risk.tier, b.risk.tier) ||
      compare(severityRank(a), severityRank(b)) ||
      compare(b.fails, a.fails) ||
      compare(staleness(a), staleness(b)) ||
      byName(a, b),
  },
  name: { direction: 'ascending', compare: byName },
  scanned: {
    direction: 'ascending',
    compare: (a, b) => compare(staleness(a), staleness(b)) || byName(a, b),
  },
  failures: {
    direction: 'descending',
    compare: (a, b) =>
      compare(b.fails, a.fails) || compare(severityRank(a), severityRank(b)) || byName(a, b),
  },
};

export const flip = (direction: 'ascending' | 'descending'): 'ascending' | 'descending' =>
  direction === 'ascending' ? 'descending' : 'ascending';
